import {
  AlertTriangle,
  CreditCard,
  Clock,
  ShieldAlert,
  Eye,
  Package,
  X,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import type { JSX } from 'react';

const ALERT_ICONS: Record<string, LucideIcon> = {
  credit: CreditCard,
  stale: Clock,
  returns: Package,
  buybox: Eye,
  hijacker: ShieldAlert,
  po_overdue: AlertTriangle,
};

const SEVERITY_STYLES: Record<string, string> = {
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
};

export interface DashboardAlert {
  id?: string | number;
  type?: string;
  severity?: string;
  message: string;
  time: string;
}

interface AlertsStripProps {
  alerts?: DashboardAlert[];
  onResolve?: (alert: DashboardAlert) => void;
  onReview?: (alert: DashboardAlert) => void;
}

export default function AlertsStrip({
  alerts = [],
  onResolve,
  onReview,
}: AlertsStripProps): JSX.Element | null {
  if (alerts.length === 0) return null;

  return (
    <div className="bg-warning/5 border border-warning/20 rounded-lg p-3 space-y-1">
      {alerts.map((alert, i) => {
        const Icon = (alert.type && ALERT_ICONS[alert.type]) || AlertTriangle;
        return (
          <div
            key={alert.id ?? i}
            className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-warning/5 transition-colors"
          >
            <Icon
              size={14}
              className={clsx(
                (alert.severity && SEVERITY_STYLES[alert.severity]) || 'text-warning',
                'flex-shrink-0'
              )}
            />
            <span className="flex-1 text-sm text-ink-primary">{alert.message}</span>
            <span className="text-2xs text-ink-muted font-mono flex-shrink-0">{alert.time}</span>
            <button
              onClick={() => onReview?.(alert)}
              className="text-2xs text-accent hover:text-accent-hover font-medium flex-shrink-0"
            >
              Review
            </button>
            <button
              onClick={() => onResolve?.(alert)}
              className="text-ink-muted hover:text-ink-primary flex-shrink-0"
              aria-label="Dismiss alert"
            >
              <X size={12} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
